import { useState, useMemo, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Col, Row, Form } from "react-bootstrap";
import FormGroupTile from "../../basic/FormGroupTile";
import AssetCard from "./AssetCard";
import toggleArrayEntry from "../../../../utils/toggleArrayEntry";
import { useFetch } from "../../../../hooks/api";
import usePagination from "../../../../hooks/usePagination";
import AssetLabel from "./AssetLabel";
import SortIcon from "./SortIcon";
import "./asset_selector.css";
import logo from "../../../../assets/images/logo/Trellis_Logomark_color.png";
import { useDebounce } from "@reactuses/core";
import DimensionFilter from "./DimensionFilter";
import { Switch } from "../../../fields/base/Switch";
import { AD_TYPES, CREATIVE_VALIDATIONS } from "../AdPlan/constants";
import { Modal } from "../../basic/Modal";
import AssetCropper from "./AssetCropper";
import { Button } from "../../basic/Button";
import AssetSummary from "./AssetSummary";
import CreativePreview from "./CreativePreview";
import { Component as ReactCrop } from "react-image-crop";

import { Accordion } from "../../basic/Accordion";
import { Checkbox } from "../../../fields/base/Checkbox";
import { SearchInput } from "../../../fields/base/TextField";
import { HStack } from "../../../../../styled-system/jsx";
import { PaginationWidget } from "../../../table/Pagination";
import { css } from "../../../../../styled-system/css";
import { CustomTriggerTooltip } from "../../basic/Tooltip";
import { FaFilter } from "react-icons/fa6";

const ControlLabel = Form.Label;

const AssetSelector = ({
  marketPlace,
  state = {},
  onChange,
  handleAssetSelection,
  selected,
  selections = [],
  errors = {},
  fieldsToValidate,
  defaultPageSize = 5,
  multiSelect = false,
  allowExpandedView = true,
  hidden,
  hideSelectionControls = false,
  // Below prop takes in array of allowed asset type labels for controlling asset type filter options
  allowedAssetTypes = null,
  initialTypeFilter,
  hidePreviewField,
  hideAssetTypeFilter = false,
  hideEligibilityFilter = false,
  hideTagsFilter = false,
  hideAdvancedFilters = false,
  hideGeneratedAssetsFilter = false,
  onUpdate,
  onTypeFilterChange,
  defaultFilters = {},
  assetGenerationSelectorType,
  overrideAssetData = null,
}) => {
  const { t } = useTranslation(["advertising", "common"]);
  const [searchFilter, setSearchFilter] = useState(state.generatedAssetNameFilter ?? "");
  const [sortBy, setSortBy] = useState("alphaAscending");
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [assetType, setAssetType] = useState(initialTypeFilter === "VIDEO" ? "video" : "image");

  const [eligibleAssetsFilter, setEligibleAssetsFilter] = useState(false);

  const [assetTypeFilter, setAssetTypeFilter] = useState(initialTypeFilter);
  const [assetSizeFilter, setAssetSizeFilter] = useState(null);
  const [assetDimensionFilter, setAssetDimensionFilter] = useState({
    width: null,
    height: null,
  });
  const [moderationStatusFilter, setModerationStatusFilter] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [assetSourceFilter, setAssetSourceFilter] = useState("all");
  const [searchByTags, setSearchByTags] = useState(false);

  const [showCropDialogue, setShowCropDialogue] = useState(false);
  const [squareCropCoordinates, setSquareCropCoordinates] = useState({});
  const [rectangleCropCoordinates, setRectangleCropCoordinates] = useState({});
  const [cropType, setCropType] = useState("square");
  const [showCreativePreview, setShowCreativePreview] = useState(false);

  const renderTagsFilter = !hideTagsFilter;
  const renderAdvancedFilters = !hideAdvancedFilters;

  const assetContainerRef = useRef(null);

  const debouncedSearchFilter = useDebounce(searchFilter, 1500);

  const { data, isLoading: isAssetDataLoading } = useFetch(
    [
      "assetSearch",
      assetTypeFilter,
      debouncedSearchFilter,
      assetSizeFilter,
      moderationStatusFilter,
      assetSourceFilter,
      assetDimensionFilter.height,
      assetDimensionFilter.width,
      sortBy,
      searchByTags,
      marketPlace,
      state.adType,
    ],
    "/assets/",
    {
      type: assetTypeFilter === "VIDEO" ? assetTypeFilter : "IMAGE",
      subtype: assetTypeFilter === "VIDEO" ? null : assetTypeFilter,
      name: !searchByTags ? debouncedSearchFilter : null,
      min_file_size:
        typeof assetSizeFilter === "object" && Number.parseInt(assetSizeFilter?.[0]) ?
          Number.parseInt(assetSizeFilter?.[0])
        : null,
      max_file_size:
        typeof assetSizeFilter === "object" && Number.parseInt(assetSizeFilter?.[1]) ?
          Number.parseInt(assetSizeFilter?.[1])
        : null,
      min_height: assetDimensionFilter.height ? Number.parseInt(assetDimensionFilter.height) : null,
      min_width: assetDimensionFilter.width ? Number.parseInt(assetDimensionFilter.width) : null,
      moderation_request_status: moderationStatusFilter !== "all" ? moderationStatusFilter : null,
      tags: searchByTags ? searchFilter : null,
      marketplace: marketPlace?.marketPlace,
      asset_source: assetTypeFilter === "LIFESTYLE_IMAGE" ? assetSourceFilter : null,
      ...defaultFilters,
    },
    {
      select: (res) => res.data ?? [],
      default: [],
      enabled: !overrideAssetData,
    },
  );
  const assetData = overrideAssetData ?? data?.results ?? [];

  let assetTypeFilterOptions = [
    {
      value: "LIFESTYLE_IMAGE",
      label: t(($) => $.advertising["Lifestyle Image"]),
      formattedName: "lifestyleImage",
    },
    {
      value: "PRODUCT_IMAGE",
      label: t(($) => $.advertising["Product Image"]),
      formattedName: "lifestyleImage",
    },
    { value: "LOGO", label: t(($) => $.advertising["Brand Logo"]), formattedName: "logo" },
    { value: "VIDEO", label: t(($) => $.advertising["Video"]), formattedName: "video" },
  ];

  // Check for existing ad, video must stay video, lifestyle image must stay lifestyle image
  if (state.id && state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value) {
    if (state.videoDetails?.asset_id || state.video) {
      assetTypeFilterOptions = [
        { value: "LOGO", label: t(($) => $.advertising["Brand Logo"]), formattedName: "logo" },
        { value: "VIDEO", label: t(($) => $.advertising["Video"]), formattedName: "video" },
      ];
    }

    if (state.lifestyleImageDetails?.asset_id) {
      assetTypeFilterOptions = [
        {
          value: "LIFESTYLE_IMAGE",
          label: t(($) => $.advertising["Lifestyle Image"]),
          formattedName: "lifestyleImage",
        },
        {
          value: "PRODUCT_IMAGE",
          label: t(($) => $.advertising["Product Image"]),
          formattedName: "lifestyleImage",
        },
        { value: "LOGO", label: t(($) => $.advertising["Brand Logo"]), formattedName: "logo" },
      ];
    }
  }

  if (allowedAssetTypes) {
    assetTypeFilterOptions = assetTypeFilterOptions.filter((type) =>
      allowedAssetTypes.some((allowedType) => allowedType === type.value),
    );

    if (assetTypeFilterOptions.length > 0 && !assetTypeFilterOptions.some((o) => o.value === assetTypeFilter)) {
      setAssetTypeFilter(assetTypeFilterOptions[0].value);
    }
  }

  const lifeStyleImageSizeOptions = [
    { value: [0, 5000000], label: t(($) => $.advertising["All"]) },
    { value: [0, 1000000], label: "0MB - 1MB" },
    { value: [1000000, 2000000], label: "1MB - 2MB" },
    { value: [2000000, 3000000], label: "2MB - 3MB" },
    { value: [3000000, 4000000], label: "3MB - 4MB" },
    { value: [4000000, 5000000], label: "4MB - 5MB" },
  ];
  const logoImageSizeOptions = [
    { value: [0, 1000000], label: t(($) => $.advertising["All"]) },
    { value: [0, 200000], label: "0kb - 200kb" },
    { value: [200000, 400000], label: "200kb - 400kb" },
    { value: [400000, 600000], label: "400kb - 600kb" },
    { value: [600000, 800000], label: "600kb - 800kb" },
    { value: [800000, 1000000], label: "800kb - 1MB" },
  ];

  const videoSizeOptions = [
    { value: [0, 50000000], label: t(($) => $.advertising["All"]) },
    { value: [0, 10000000], label: "0MB - 10MB" },
    { value: [10000000, 20000000], label: "10MB - 20MB" },
    { value: [20000000, 30000000], label: "20MB - 30MB" },
    { value: [30000000, 40000000], label: "30MB - 40MB" },
    { value: [40000000, 50000000], label: "40MB - 50MB" },
  ];

  const moderationStatusOptions = [
    { value: "all", label: t(($) => $.advertising["All"]) },
    { value: "COMPLETED", label: t(($) => $.advertising["Completed"]) },
    { value: "SKIPPED", label: t(($) => $.advertising["Skipped"]) },
  ];

  const assetSourceOptions = [
    { value: "all", label: t(($) => $.advertising["All"]) },
    { value: "STOCK", label: t(($) => $.advertising["Stock"]) },
    { value: "GENERATED", label: t(($) => $.advertising["Generated"]) },
  ];

  const filteredAssets = useMemo(() => {
    let sortedAssets = assetData;
    if (sortBy === "alphaAscending") {
      sortedAssets = assetData?.sort((a, b) =>
        a.name?.toLowerCase() < b.name?.toLowerCase() ? -1
        : a.name?.toLowerCase() > b.name?.toLowerCase() ? 1
        : 0,
      );
    }

    if (sortBy === "alphaDescending") {
      sortedAssets = assetData?.sort((a, b) =>
        a.name?.toLowerCase() > b.name?.toLowerCase() ? -1
        : a.name?.toLowerCase() < b.name?.toLowerCase() ? 1
        : 0,
      );
    }

    if (assetTypeFilter === "VIDEO" && eligibleAssetsFilter) {
      sortedAssets = sortedAssets.filter((asset) => {
        const { approved_programs } = asset;

        if (state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value) {
          return approved_programs.includes("SPONSORED_BRANDS_VIDEO");
        }

        if (state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value) {
          return approved_programs.includes("SPONSORED_DISPLAY_VIDEO");
        }

        return true;
      });
    }

    if (overrideAssetData && debouncedSearchFilter?.length) {
      sortedAssets = sortedAssets?.filter((asset) => {
        return (
          asset.name.toLowerCase().includes(debouncedSearchFilter.toLowerCase()) ||
          asset.theme?.themeForDisplay?.toLowerCase().includes(debouncedSearchFilter.toLowerCase()) ||
          asset.tags?.join(" ")?.toLowerCase().includes(debouncedSearchFilter.toLowerCase())
        );
      });
    }

    return sortedAssets;

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, assetData, pageSize, searchByTags, assetType, eligibleAssetsFilter, debouncedSearchFilter]);

  const { pageData, page, setPage, lastPage } = usePagination(filteredAssets, pageSize);
  const onAssetSelection = (asset) => {
    const formattedAssetType =
      assetType === "video" ?
        assetTypeFilter === "LOGO" ?
          "logo"
        : "video"
      : assetTypeFilterOptions?.find((filter) => filter.value === assetTypeFilter)?.formattedName;

    if (multiSelect) {
      const updatedSelections = toggleArrayEntry(asset?.asset_id, selections);
      const updatedSelectionIds = updatedSelections.filter((asset) => asset);
      handleAssetSelection(updatedSelectionIds, updatedSelections, formattedAssetType);
      return;
    }

    if (!asset) {
      if (state.videoDetails) {
        handleAssetSelection(null, null, "video");
        return;
      }
      handleAssetSelection(null, null, "lifestyleImage");
      return;
    }

    handleAssetSelection(asset.asset_id, asset, formattedAssetType);

    if (
      state.lifestyleImageDetails?.asset_id !== asset.asset_id &&
      state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value &&
      ["LIFESTYLE_IMAGE", "PRODUCT_IMAGE"].includes(assetTypeFilter)
    ) {
      setShowCropDialogue(true);
    }
  };

  const assetOptions = useMemo(() => {
    const options =
      state.lifestyleImageDetails ?
        [state.lifestyleImageDetails]?.map((asset) => {
          return {
            value: asset.asset_id,
            url: asset.name,
            label: <AssetLabel asset={asset} />,
          };
        })
      : state.videoDetails ?
        [state.videoDetails]?.map((asset) => {
          return {
            value: asset.asset_id,
            url: asset.name,
            label: <AssetLabel asset={asset} />,
          };
        })
      : [];

    return options;

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, state.lifestyleImageDetails]);

  const validations =
    CREATIVE_VALIDATIONS?.[state.adType]?.[cropType === "square" ? "squareImage" : "lifestyleImage"] ?? {};
  const croppedAspectRatio =
    cropType === "rectangle" ?
      state.sdRectangleImageCroppingCoordinates.width / state.sdRectangleImageCroppingCoordinates.height
    : null;

  useEffect(() => {
    setPage(0);

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [
    assetTypeFilter,
    sortBy,
    searchFilter,
    assetSizeFilter,
    assetDimensionFilter,
    pageSize,
    searchByTags,
    assetSourceFilter,
    moderationStatusFilter,
  ]);

  useEffect(() => {
    if (!showAdvancedFilters && (assetSizeFilter || moderationStatusFilter) && !eligibleAssetsFilter) {
      setAssetSizeFilter(null);
      setModerationStatusFilter(null);
    }
  }, [showAdvancedFilters, assetSizeFilter, moderationStatusFilter, eligibleAssetsFilter]);

  useEffect(() => {
    if (assetType === "video" && assetTypeFilter !== "video") {
      setAssetTypeFilter("VIDEO");
      return;
    }
    if (assetType === "image") {
      setAssetTypeFilter(initialTypeFilter ?? "LIFESTYLE_IMAGE");
      return;
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [assetType]);

  useEffect(() => {
    if (state.assetSelectionType) {
      const updatedTypeFilter = assetTypeFilterOptions.find(
        (type) => type.formattedName === state.assetSelectionType,
      )?.value;

      setAssetTypeFilter(updatedTypeFilter);
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [state.assetSelectionType]);

  // Clear cropping coordinates when lifestyle image changes
  useEffect(() => {
    if (
      (selected !== state.lifestyleImageDetails?.asset_id || !selected) &&
      ["LIFESTYLE_IMAGE", "PRODUCT_IMAGE"].includes(assetTypeFilter) &&
      state.selectedType === "lifestyleImageDetails"
    ) {
      onChange({
        sdSquareImageCroppingCoordinates: {},
        sdRectangleImageCroppingCoordinates: {},
        sdSquareCroppedArea: null,
        sdRectangleCroppedArea: null,
      });
      setSquareCropCoordinates({});
      setRectangleCropCoordinates({});
      setCropType("square");
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    const validations = CREATIVE_VALIDATIONS[state.adType];

    if (eligibleAssetsFilter) {
      let assetValidations = [];
      if (["LIFESTYLE_IMAGE", "PRODUCT_IMAGE"].includes(assetTypeFilter)) {
        assetValidations = validations.lifestyleImage;
      }
      if (["LOGO"].includes(assetTypeFilter)) {
        assetValidations = validations.logo;
      }
      const { minHeight, minWidth, maxFileSize } = assetValidations;

      setAssetSizeFilter([0, parseInt(maxFileSize)]);
      setAssetDimensionFilter(() => ({
        width: minWidth,
        height: minHeight,
      }));
    } else {
      // Clear filters to show all assets
      setAssetSizeFilter(null);
      setAssetDimensionFilter(() => ({
        width: null,
        height: null,
      }));
    }
  }, [eligibleAssetsFilter, assetTypeFilter, state.adType]);

  if (hidden) {
    return null;
  }

  // Below makes sure asset options are always visible after users scrolled down in form container
  assetContainerRef.current &&
    !isAssetDataLoading &&
    (page > 1 || debouncedSearchFilter) &&
    !state.generatedAssetNameFilter &&
    assetContainerRef.current.scrollIntoView();

  return (
    <div>
      <Row style={{ marginTop: "2rem", marginBottom: "1rem" }}>
        <Col md={6} className="pe-5">
          <div style={{ textAlign: "left", marginBottom: "1rem" }}>
            <ControlLabel>
              <h6
                className="title fs-4"
                style={{
                  color: "#73706E",
                  fontWeight: "700",
                  textTransform: "none",
                }}
              >
                {t(($) => $.advertising["Image Search"])}
              </h6>
            </ControlLabel>
          </div>
          <HStack>
            <SearchInput
              value={searchFilter}
              onChange={setSearchFilter}
              aria-label={t(($) => $.advertising["Search for assets"])}
              placeholder={
                assetGenerationSelectorType ? t(($) => $.advertising["Search for assets by name, tag, or theme"])
                : searchByTags ?
                  t(($) => $.advertising["Search for assets by tags"])
                : t(($) => $.advertising["Search for assets by name"])
              }
            />
            {renderTagsFilter && (
              <Checkbox
                label={t(($) => $.advertising["Tags"])}
                value={searchByTags}
                onChange={() => {
                  setSearchByTags(!searchByTags);
                }}
              />
            )}
          </HStack>

          {!hideGeneratedAssetsFilter && assetTypeFilter === "LIFESTYLE_IMAGE" && (
            <div className="d-flex align-items-center justify-content-start pt-4">
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  margin: "auto 1rem auto 0",
                }}
              >
                {t(($) => $.advertising["Asset Source"])}
              </p>
              <select
                onChange={(e) => setAssetSourceFilter(e.target.value)}
                value={assetSourceFilter}
                className="p-2 border rounded fs-5"
              >
                {assetSourceOptions.map((type, index) => (
                  <option key={index} value={type?.value}>
                    {type?.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </Col>
        <Col xs={12} md={6} className="ps-5">
          {!hidePreviewField && (
            <FormGroupTile
              type="select"
              stateName={"logo"}
              handleChildFormElement={(key, value) => {
                if (value?.label?.props?.asset) {
                  onAssetSelection(value.label.props.asset);
                } else {
                  onAssetSelection(null);
                }
              }}
              selectClasses="lightly-disabled"
              labelClassName="w-100"
              customLabelStyles={{ textAlign: "left", marginBottom: ".6rem" }}
              label={
                <div className="d-flex fs-4">
                  <h6
                    className="title fs-4 pe-5 pt-2"
                    style={{
                      color: "#73706E",
                      textTransform: "none",
                      fontWeight: "700",
                    }}
                  >
                    {state.videoDetails || (!state.lifestyleImageDetails && assetType === "video") ?
                      t(($) => $.advertising["Video"])
                    : t(($) => $.advertising["Lifestyle Image (optional)"])}
                  </h6>
                </div>
              }
              isClearable={true}
              isSearchable={false}
              defaultValue={
                multiSelect ?
                  selections.some((selectedAssetId) =>
                    assetOptions?.find((asset) => asset.value === selectedAssetId),
                  )?.[0]
                : assetOptions?.[0]
              }
              // Below two props used to display read-only field without disabled styles, while still allowing clear and select events
              components={{
                Menu: () => null,
                MenuList: () => null,
                DropdownIndicator: () => null,
                IndicatorSeparator: () => null,
              }}
              placeholder={null}
              options={assetOptions}
              multiSelect={multiSelect}
              onClick={() => {
                onChange({
                  assetSelectionType: assetType === "video" ? "video" : "lifestyleImage",
                });
              }}
            />
          )}
          {errors.lifestyleImage && fieldsToValidate?.includes("lifestyleImage") && (
            <div className="pt-4">
              <small className="text-danger">{errors.lifestyleImage}</small>
            </div>
          )}
          <div
            style={{
              visibility: state.adType === AD_TYPES.SPONSORED_BRAND_VIDEO.value ? "hidden" : "inherit",
            }}
            className={`d-flex ${
              (
                (state.sdSquareImageCroppingCoordinates?.height || state.sdSquareImageCroppingCoordinates?.width) &&
                state.lifestyleImageDetails
              ) ?
                "justify-content-between"
              : "justify-content-end"
            } pt-4 pe-3`}
          >
            {(state.sdSquareImageCroppingCoordinates?.height || state.sdSquareImageCroppingCoordinates?.width) &&
              state.lifestyleImageDetails && (
                <div className="d-flex justify-content-end align-items-center">
                  <Button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowCropDialogue(true);
                    }}
                    css={{ transform: "none" }}
                  >
                    {t(($) => $.advertising["Edit Cropping"])}
                  </Button>
                </div>
              )}

            <div className="d-flex align-items-center">
              {state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && (
                <HStack
                  className={css({ fontSize: "xsmall", fontWeight: "600", color: "#73706E", paddingRight: "40" })}
                >
                  {t(($) => $.advertising["Images"])}
                  <Switch
                    isSelected={assetType === "video"}
                    onChange={() => setAssetType(assetType === "video" ? "image" : "video")}
                  />
                  {t(($) => $.advertising["Videos"])}
                </HStack>
              )}
              {!hideEligibilityFilter && (
                <HStack className={css({ fontSize: "xsmall", fontWeight: "600", color: "#73706E" })}>
                  {t(($) => $.advertising["All Assets"])}
                  <Switch
                    value={eligibleAssetsFilter}
                    onChange={() => {
                      setEligibleAssetsFilter(!eligibleAssetsFilter);
                    }}
                    wrapperClass="assetTypeSwitch"
                  />
                  {t(($) => $.advertising["Eligible"])}
                </HStack>
              )}
            </div>
          </div>
        </Col>
      </Row>
      {(state.lifestyleImageDetails || state.logoDetails || state.videoDetails) && (
        <Row>
          <Accordion
            title={
              <span
                style={{
                  fontSize: "16px",
                  fontWeight: 500,
                  marginRight: ".5rem",
                  color: "#152748",
                  textTransform: "none",
                }}
              >
                {t(($) => $.advertising["View Selected Assets"])}
              </span>
            }
            marginTop="0"
          >
            <Row>
              <AssetSummary
                assetId={state.logoDetails?.asset_id}
                assetDescriptor={t(($) => $.advertising["Brand Logo"])}
                hidden={!state.logoDetails?.asset_id}
                assetType={state.logoDetails?.type}
                summaryContainerClasses={"pe-5 mt-1"}
              />
              <AssetSummary
                assetId={state.lifestyleImageDetails?.asset_id}
                assetDescriptor={t(($) => $.advertising["Lifestyle Image"])}
                hidden={!state.lifestyleImageDetails?.asset_id || state.sdRectangleCroppedArea}
                assetType={state.lifestyleImageDetails?.type}
                summaryContainerClasses={"pe-5 mt-1"}
              />
              {state.sdRectangleCroppedArea && (
                <Col
                  xs={12}
                  lg={6}
                  className={`${state.logoDetails?.asset_id ? "ps-5" : "pe-5"} mt-1 col-lg-6 col-12"`}
                >
                  <h6
                    className="title mb-4 fs-4"
                    style={{
                      color: "#73706E",
                      fontWeight: "700",
                      textTransform: "none",
                    }}
                  >
                    {t(($) => $.advertising["Lifestyle Image (Cropped)"])}
                  </h6>
                  <ReactCrop crop={state.sdRectangleCroppedArea} zoom={1}>
                    <img
                      src={state.lifestyleImageDetails?.url_default}
                      alt={t(($) => $.advertising["Preview of cropped asset."])}
                      crossOrigin="anonymous"
                    />
                  </ReactCrop>
                </Col>
              )}
              <AssetSummary
                assetId={state.videoDetails?.asset_id}
                assetDescriptor={t(($) => $.advertising["Brand Video"])}
                assetType={"VIDEO"}
                hidden={!state.videoDetails?.asset_id}
                columnSpan={6}
                summaryContainerClasses={state.logoDetails ? "ps-5 mt-5" : "pe-5 mt-1"}
              />

              {state.sdSquareCroppedArea && (
                <Col
                  xs={12}
                  lg={6}
                  className={`${
                    state.logoDetails?.asset_id && state.lifestyleImageDetails?.asset_id ? "pe-5 pt-5" : "ps-5"
                  } mt-1 col-lg-6 col-12"`}
                >
                  <h6
                    className="title mb-4 fs-4"
                    style={{
                      color: "#73706E",
                      fontWeight: "700",
                      textTransform: "none",
                    }}
                  >
                    {t(($) => $.advertising["Lifestyle Image (Square)"])}
                  </h6>
                  <ReactCrop crop={state.sdSquareCroppedArea} zoom={1} aspect={1}>
                    <img
                      src={state.lifestyleImageDetails?.url_default}
                      alt={t(($) => $.advertising["Preview of cropped asset."])}
                      crossOrigin="anonymous"
                    />
                  </ReactCrop>
                </Col>
              )}
            </Row>
          </Accordion>
        </Row>
      )}
      {state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && (
        <Accordion
          title={
            <span
              style={{
                fontSize: "16px",
                fontWeight: 500,
                marginRight: ".5rem",
                color: "#152748",
                textTransform: "none",
              }}
            >
              {t(($) => $.advertising["Creative Preview"])}
            </span>
          }
          marginTop="2rem"
          onExpandedChange={(expanded) => {
            setShowCreativePreview(expanded);
          }}
        >
          {showCreativePreview && (
            <Row>
              <CreativePreview
                state={state}
                // Creative preview endpoint doesn't return HTML consistently with more than 1 ASIN, for now just include first selected
                productId={state.product?.[0]}
                marketPlace={marketPlace?.marketPlace}
                showCreativePreview={showCreativePreview}
                accordionLayout={true}
              />
            </Row>
          )}
        </Accordion>
      )}

      <div ref={assetContainerRef} />
      <Row
        style={{
          marginTop: "1rem",
          marginBottom: "2rem",
          display: showCropDialogue ? "none" : "inherit",
        }}
        className="d-flex justify-content-between"
      >
        <Col xs={12} md={4} className="d-flex align-items-center">
          <span
            style={{
              fontSize: "16px",
              fontWeight: 500,
              marginRight: ".5rem",
            }}
          >
            {t(($) => $.advertising["Search Results"])}
          </span>
          <div>
            <SortIcon sortBy={sortBy} setSortBy={setSortBy} />
          </div>
          {renderAdvancedFilters && (
            <CustomTriggerTooltip toolTip={t(($) => $.common["Toggle advanced filters"])}>
              <button
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                style={{
                  backgroundColor: "#FFF",
                  borderRadius: "100%",
                  border: "none",
                  width: "25px",
                  height: "25px",
                  position: "relative",
                }}
              >
                <FaFilter
                  className={css({
                    color: "grey",
                    display: "inline",
                    cursor: "pointer",
                  })}
                />
              </button>
            </CustomTriggerTooltip>
          )}
        </Col>
        {!hideAssetTypeFilter && (
          <Col xs={12} md={4} className="d-flex align-items-center justify-content-center">
            <>
              <p
                style={{
                  fontSize: "14px",
                  fontWeight: 500,
                  margin: "auto 1rem auto 0",
                }}
              >
                {t(($) => $.advertising["Asset Type"])}
              </p>
              <select
                onChange={(e) => {
                  const updatedTypeFilter = e.target.value;
                  setAssetTypeFilter(updatedTypeFilter);
                  setAssetSourceFilter("all");
                  setSearchFilter("");
                  const formattedType = assetTypeFilterOptions.find(
                    (type) => type.value === updatedTypeFilter,
                  )?.formattedName;
                  if (formattedType) {
                    onTypeFilterChange?.(formattedType);
                  }
                }}
                value={assetTypeFilter}
                className="p-2 border rounded fs-5"
              >
                {assetTypeFilterOptions.map((type, index) => (
                  <option key={index} value={type?.value}>
                    {type?.label}
                  </option>
                ))}
              </select>
            </>
          </Col>
        )}
        <Col xs={12} md={4} className="d-flex align-items-center justify-content-end">
          {!assetGenerationSelectorType && (
            <PaginationWidget
              hideResultsCount
              pageSizeOptions={[4, 8, 12, 16]}
              currentPage={page}
              setCurrentPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              totalPages={lastPage}
              pageSizeLabel={(size) => t(($) => $.advertising["{size} items"], { size })}
            />
          )}
        </Col>
      </Row>

      {showAdvancedFilters && (
        <Row>
          <Col xs={12} md={4} className="d-flex align-items-center justify-content-start">
            <p
              style={{
                fontSize: "14px",
                fontWeight: 500,
                margin: "auto 1rem auto 0",
              }}
            >
              {t(($) => $.advertising["Asset Size"])}
            </p>
            <select
              onChange={(e) => setAssetSizeFilter(e.target.value.split(","))}
              value={assetSizeFilter}
              className="p-2 border rounded fs-5"
            >
              {assetTypeFilter === "VIDEO" &&
                videoSizeOptions.map((type, index) => (
                  <option key={index} value={type?.value}>
                    {type?.label}
                  </option>
                ))}
              {(assetTypeFilter === "LIFESTYLE_IMAGE" || assetTypeFilter === "PRODUCT_IMAGE") &&
                lifeStyleImageSizeOptions.map((type, index) => (
                  <option key={index} value={type?.value}>
                    {type?.label}
                  </option>
                ))}
              {assetTypeFilter === "LOGO" &&
                logoImageSizeOptions.map((type, index) => (
                  <option key={index} value={type?.value}>
                    {type?.label}
                  </option>
                ))}
            </select>
          </Col>
          <Col xs={12} md={4} className="d-flex align-items-center justify-content-center">
            <p
              style={{
                fontSize: "14px",
                fontWeight: 500,
                margin: "auto 1rem auto 0",
              }}
            >
              {t(($) => $.advertising["Dimensions"])}
            </p>
            <div style={{ minWidth: "140px" }} className="d-flex">
              <DimensionFilter
                assetDimensionFilter={assetDimensionFilter}
                setAssetDimensionFilter={setAssetDimensionFilter}
              />
            </div>
          </Col>
          <Col xs={12} md={4} className="d-flex align-items-center justify-content-end">
            <p
              style={{
                fontSize: "14px",
                fontWeight: 500,
                margin: "auto 1rem auto 0",
              }}
            >
              {t(($) => $.advertising["Moderation Status"])}
            </p>
            <select
              onChange={(e) => setModerationStatusFilter(e.target.value)}
              value={moderationStatusFilter}
              className="p-2 border rounded fs-5"
            >
              {moderationStatusOptions.map((type, index) => (
                <option key={index} value={type?.value}>
                  {type?.label}
                </option>
              ))}
            </select>
          </Col>
        </Row>
      )}
      {/* Asset card container */}
      <Row>
        {isAssetDataLoading ?
          <Row>
            <Col xs={12} className="d-flex justify-content-center align-items-center assets-loading-container">
              <img src={logo} alt={t(($) => $.common["Loading..."])} className="rotate" width="64" />
            </Col>
          </Row>
        : <>
            {pageData?.length ?
              pageData?.map((asset) => (
                <AssetCard
                  key={asset.id}
                  onAssetSelection={onAssetSelection}
                  isSelected={
                    multiSelect ?
                      selections.some((selectedAssetId) => selectedAssetId === asset.asset_id)
                    : selected === asset.asset_id
                  }
                  asset={asset}
                  isVideo={assetTypeFilter === "VIDEO"}
                  allowExpandedModal={allowExpandedView}
                  adType={state.adType}
                  assetTypeFilter={assetTypeFilter}
                  hideSelectionControls={hideSelectionControls}
                  assetGenerationSelectorType={assetGenerationSelectorType}
                />
              ))
            : <Row>
                <p
                  className="text-center"
                  style={{
                    color: "#73706E",
                    fontWeight: "700",
                    marginTop: "100px",
                  }}
                >
                  {assetType === "video" ?
                    t(($) => $.advertising["No videos found"])
                  : t(($) => $.advertising["No images found"])}
                </p>
              </Row>
            }
          </>
        }
      </Row>

      <Modal
        isOpen={showCropDialogue}
        isDismissable={false}
        closeModal={() => setShowCropDialogue(false)}
        onOpenChange={(isOpen) => {
          setShowCropDialogue(isOpen);
        }}
        title={t(($) => $.advertising["Lifestyle Image Cropping"])}
        css={{ width: "1200px", margin: 0, paddingTop: "50px" }}
      >
        <Row className="d-flex justify-content-center align-content-center" style={{ height: "78vh" }}>
          <Col xs={12} className="h-100">
            <AssetCropper
              assetDetails={state.lifestyleImageDetails}
              // Note coordinates are calculated relative to natural asset dimensions, cropped area based on rendered dimensions for editing
              updateCropDimensions={(coordinates, croppedArea) => {
                if (cropType === "square") {
                  if (coordinates["width"] !== coordinates["height"]) {
                    coordinates["width"] = coordinates["height"];
                  }
                  setSquareCropCoordinates(coordinates);

                  onChange({
                    sdSquareImageCroppingCoordinates: coordinates,
                    sdSquareCroppedArea: croppedArea,
                  });
                }
                if (cropType === "rectangle") {
                  setRectangleCropCoordinates(coordinates);
                  onChange({
                    sdRectangleImageCroppingCoordinates: coordinates,
                    sdRectangleCroppedArea: croppedArea,
                  });
                }
              }}
              // Eventually below should handle all SB/SD types that we want to support cropping for
              validations={validations}
              cropType={cropType}
              setCropType={setCropType}
              initialCrop={
                cropType === "square" ? state.sdSquareCroppedArea
                : cropType === "rectangle" ?
                  state.sdRectangleCroppedArea
                : { x: 0, y: 0, aspect: 1 }
              }
              rectangleLifestyleImageAspectRatio={cropType === "rectangle" ? croppedAspectRatio : null}
            />
            <HStack justifyContent={"center"}>
              <Button
                onPress={() => {
                  onAssetSelection(null);
                  setShowCropDialogue(false);
                }}
                className={css({ marginRight: 8 })}
              >
                {t(($) => $.common.actions.Cancel)}
              </Button>
              <Button
                onPress={() => {
                  setShowCropDialogue(false);
                }}
                // TODO: consts for SD validations specifically
                isDisabled={
                  ((squareCropCoordinates?.height < 628 ||
                    squareCropCoordinates?.width < 628 ||
                    !squareCropCoordinates?.height) &&
                    state.lifestyleImageDetails?.file_meta_data?.height !==
                      state.lifestyleImageDetails?.file_meta_data?.width) ||
                  rectangleCropCoordinates?.height < 628 ||
                  rectangleCropCoordinates?.width < 1200 ||
                  (validations.aspectRatio && croppedAspectRatio < validations.aspectRatio)
                }
              >
                {t(($) => $.advertising["Confirm Cropping"])}
              </Button>
            </HStack>
          </Col>
        </Row>
      </Modal>
      {state.adType === AD_TYPES.SPONSORED_DISPLAY_V2.value && state.id && (
        <Button
          onPress={onUpdate}
          isDisabled={errors?.lifestyleImage || errors?.video || errors?.logo || errors?.headline}
        >
          {t(($) => $.advertising["Update Creatives"])}
        </Button>
      )}
    </div>
  );
};

export default AssetSelector;
